import React, { Component } from 'react'
import componentQueries from 'react-component-queries'
import classnames from 'classnames'
import { renderToStaticMarkup } from 'react-dom/server'
import { withLocalize } from 'react-localize-redux'

import englishTranslation from './localization/en.translation.json'
import frenchTranslation from './localization/fr.translation.json'
import frenchCanadianTranslation from './localization/fr-ca.translation.json'
import spanishTranslations from './localization/es.translation.json'
import './theme/theme.module.scss'
import styles from './Profile.module.scss'
import { initializeTimings } from './utility/timings'
import Loadable from 'react-loadable'
import './common-components'
import PropertyStore from './utility/propertyStore'
import { trackGoal, trackEvent } from 'fathom-client'
import DataStore from './utility/dataStore'
import getFathomEventByKey from './utility/fathomEventsMap'
import { loadableComponents, findTarget } from './utility/componentLoading'
import { createStyleTag } from './utility'

function requireFeature(feature) {
  try {
    return import('./components/' + feature + '/index.js')
  } catch (err) {
    console.error(
      "'" + feature + "' was requested as a feature but was not found."
    )
  }
}

class Profile extends Component {
  constructor(props) {
    super(props)
    this.loadedComponents = []
    this.allLoaded = false
    this.sendAnalytics = this.sendAnalytics.bind(this)
    // Support the "components" in the new data model
    // as well as the "features" from the old placeholder strategy
    const components =
      'components' in props
        ? props.components
        : props.features.map((f) => ({ name: f }))
    this.components = components
      .map((c, index) => ({
        ...c,
        Component: Loadable({
          loader: () => requireFeature(c.name),
          render: (loaded, props) => {
            const Component = loaded.default

            return Component ? (
              <ErrorBoundary>
                <Component
                  {...props}
                  propertyStore={this.propertyStore}
                  soldPropertyStore={this.soldPropertyStore}
                  dataStore={this.dataStore}
                />
              </ErrorBoundary>
            ) : (
              <div />
            )
          },
          loading: ({ error, retry }) => {
            if (error && (c.retries || 0) < 5) {
              c.retries = (c.retries || 0) + 1
              retry()
            }
            return <div />
          }
        })
      }))
      .filter((c) => c.Component)

    this.propertyStore = new PropertyStore(
      props.neighborhood._id,
      props.propertyActiveStatuses,
      props.ActiveMongoProperties || props.MongoProperties
        ? 'mongo'
        : 'postgres',
      props.mapTotalPropertyLimit
    )
    this.soldPropertyStore = new PropertyStore(
      props.neighborhood._id,
      'Closed',
      props.ActiveMongoProperties || props.MongoProperties
        ? 'mongo'
        : 'postgres'
    )
    this.dataStore = new DataStore(props.neighborhood._id, props.propertyActiveStatuses)

    if (props.maximumNumberOfProperties) {
      this.propertyStore.setMaximumListingCount(
        Math.abs(props.maximumNumberOfProperties - 200)
      )
    }

    if (props.propertyTypes) {
      // Handle propertyTypes as a string or an Array
      const types = String(props.propertyTypes)
        .split(',')
        .map((t) => t.trim())
      this.propertyStore.setTypes(types)
      this.soldPropertyStore.setTypes(types)
      this.dataStore.setTypes(types)
    }
    if (props.propertySubTypes) {
      const subTypes = String(props.propertySubTypes)
        .split(',')
        .map((t) => t.trim())
      this.propertyStore.setSubTypes(subTypes)
      this.soldPropertyStore.setSubTypes(subTypes)
      this.dataStore.setSubTypes(subTypes)
    }

    if (props.brokerage.mls || props.mls) {
      this.updateMLS(props)
    }
    if (props.neighborhood && props.neighborhood.geometry) {
      this.propertyStore.setGeometry(props.neighborhood.geometry)
      this.soldPropertyStore.setGeometry(props.neighborhood.geometry)
    }
    if (props.minimumHomePrice) {
      this.propertyStore.setMinimumHomePrice(props.minimumHomePrice)
      this.soldPropertyStore.setMinimumHomePrice(props.minimumHomePrice)
      this.dataStore.setMinimumHomePrice(props.minimumHomePrice)
    }

    props.initialize({
      languages: this.availableLanguages,
      options: {
        renderToStaticMarkup: (i) => typeof i === 'string' ? i : renderToStaticMarkup(i),
        defaultLanguage: 'en',
        onMissingTranslation: ({ defaultTranslation }) => defaultTranslation
      }
    })
    this.props.addTranslationForLanguage(englishTranslation, 'en')
    this.props.addTranslationForLanguage(frenchTranslation, 'fr')
    this.props.addTranslationForLanguage(frenchCanadianTranslation, 'fr-ca')
    this.props.addTranslationForLanguage(spanishTranslations, 'es')
    const scriptLang = window.liveby.language
    const userLang = navigator.language || navigator.userLanguage
    const htmlLang = document.documentElement.lang
    let lang
    if (htmlLang.includes('-')) {
      lang = htmlLang.split('-')[0]
    } else {
      lang = htmlLang
    }
    let requestedLang = 'en'
    // if the language is set on the script
    if (scriptLang) {
      requestedLang = scriptLang
    } else if (this.languageAvailable(props.language)) {
      requestedLang = props.language
      // if the language is set on the html tag
    } else if (this.languageAvailable(lang)) {
      requestedLang = lang
      // if the language of the users browser is set
    } else if (this.languageAvailable(userLang)) {
      requestedLang = userLang
    }
    this.props.setActiveLanguage(requestedLang)
  }
  availableLanguages = ['en', 'fr', 'fr-ca', 'es']
  languageAvailable(language) {
    return this.availableLanguages.some((x) => x === language)
  }

  updateMLS(props) {
    let mls = []

    if (props.mls) {
      mls = props.mls.split(',').map((m) => m.trim())
    } else if (props.brokerage.mls) {
      mls = props.brokerage.mls
    }

    this.propertyStore.setMls(mls)
    this.soldPropertyStore.setMls(mls)
    this.dataStore.setMls(mls)
  }

  componentDidCatch(error, info) {
    console.log(this.props.header)
    console.error(error)
    fetch(
      `https://${process.env.REACT_APP_API_ENDPOINT}/v1/client-error-reporter`,
      {
        method: 'POST',
        body: JSON.stringify({
          error: `${error.stack}\n\n${info.componentStack}`
        })
      }
    )
  }

  componentDidUpdate(prevProps) {
    if (this.props.neighborhood.geometry && !prevProps.neighborhood.geometry) {
      this.propertyStore.setGeometry(this.props.neighborhood.geometry)
      this.soldPropertyStore.setGeometry(this.props.neighborhood.geometry)
    }
    if (
      (this.props.brokerage.mls && !prevProps.brokerage.mls) ||
      (this.props.mls && !prevProps.mls)
    ) {
      this.updateMLS(this.props)
    }
  }

  componentDidMount() {
    initializeTimings()

    if (this.props.neighborhood.geometry) {
      this.propertyStore.setGeometry(this.props.neighborhood.geometry)
      this.soldPropertyStore.setGeometry(this.props.neighborhood.geometry)
    }
    this.startLoadedEventFallback()
    setTimeout(() => {
      this.startPageLoadedListener()
    }, 750)
  }

  startPageLoadedListener() {
    if (this.allTemplateComponentsHaveLoaded()) {
      this.sendLoadedEvent()
    }

    if (document.readyState === 'ready' || document.readyState === 'complete') {
      const location = this.props ? findTarget(this.props.selector) : null
      if (!this.allLoaded && !location && this.loadedComponents.length === 0) {
        this.sendLoadedEvent()
      }
    } else {
      document.onreadystatechange = function () {
        const location =
          this.props && this.props.selector
            ? findTarget(this.props.selector)
            : null

        if (
          document.readyState == 'complete' &&
          !location &&
          this.loadedComponents
            ? this.loadedComponents.length === 0
            : true
        ) {
          this.sendLoadedEvent()
        }
      }.bind(this)
    }
  }

  startLoadedEventFallback() {
    setTimeout(() => {
      if (!this.allLoaded) {
        this.sendLoadedEvent()
      }
    }, 4000)
  }

  /**
   * Set global prop to be shared among components.
   * Use with the same syntax as setState.
   * @param args.values {Object} required
   */
  setProp(values) {
    const event = new CustomEvent('livebyPropEvent', {
      detail: values
    })
    window.dispatchEvent(event)
  }

  /**
   * Fathom analytics event tracking. Create a new event in the Fathom Analytics dashboard and add the event id to the fathomEventsMap file.
   * Use this function to track certain events/actions are made on certain components.
   *
   * @param args.componentId {String} The id of the component we're tracking events on. (e.g. external-link, market-trends, lbl-link, etc)
   * @param args.action {String} The action taking place. (e.g. Clicked Pill, Clicked Send Message, etc)
   * @param args.additionalGroupings (optional) Any additional groupings that are being tracked.
   */
  sendAnalytics(componentId, action, ...additionalGroupings) {
    if (process.env.NODE_ENV !== 'production') {
      return
    }
    const key =
      componentId +
      ' > ' +
      action +
      (additionalGroupings.length
        ? ' > ' + additionalGroupings.join(' > ')
        : '')
    const event = getFathomEventByKey(key)
    if (event) {
      trackGoal(event[0] || event, event[1])
    } else {
      trackEvent(key.toString())
    }
  }

  allTemplateComponentsHaveLoaded() {
    const templateLoadableComponents = this.components.filter((component) =>
      loadableComponents.some((n) => n === component.name)
    )

    return this.loadedComponents.length >= templateLoadableComponents.length
  }

  componentLoaded(name, height) {
    if (
      height &&
      height > 0 &&
      !this.loadedComponents.find((x) => x.name === name)
    ) {
      this.addToLoadedComponents(name, height)
      this.checkMainPageLoaded(name, height)
    }
  }

  checkMainPageLoaded(name, height) {
    const windowHeight = window.innerHeight
    let loadedHeight = 0
    for (const component of this.components) {
      const loadedComponent = this.hasComponentLoadedYet(component)
      if (loadedComponent) {
        loadedHeight += loadedComponent.height

        if (
          loadedHeight >= windowHeight ||
          this.allTemplateComponentsHaveLoaded()
        ) {
          this.sendLoadedEvent()
        }
      }
    }
  }

  hasComponentLoadedYet(component) {
    return this.loadedComponents
      ? this.loadedComponents.find((x) => x.name === component.name)
      : undefined
  }

  addToLoadedComponents(name, height) {
    this.loadedComponents.push({ name, height })
  }

  sendLoadedEvent() {
    this.allLoaded = true
    const event = new CustomEvent('livebyLoaded', {
      detail: this.props.selector
    })
    window.dispatchEvent(event)
  }

  render() {
    const properties = this.props.neighborhood.properties || {}
    const { language: lang } = this.props

    window.addEventListener(
      'livebyPropEvent',
      (event) => {
        this.setState(event.detail)
      },
      false
    )

    window.addEventListener(
      'livebyComponentLoaded',
      (event) => {
        const inThisContentArea = this.components
          .map((c) => c.name)
          .includes(event.detail.name)
        if (!this.allLoaded && inThisContentArea) {
          this.componentLoaded(event.detail.name, event.detail.height)
        }
      },
      false
    )
    let styles = ''
    const needShadowCSS =
      (this.props.selector && this.props.selector.split('|').length > 1) ||
      window.liveby.useShadowDom
    if (needShadowCSS) {
      styles = createStyleTag(
        this.props.template?.adminStyles,
        this.props.brokerage?.styles,
        true
      )
    }
    return (
      <article
        className={classnames(styles.profile, this.props.queryClassNames)}
        itemScope
        itemType='https://schema.org/Place'
        {...{ lang }} // Include `lang` attribute only if language is set.
      >
        {properties.label && (
          <meta itemProp='name' content={properties.label} />
        )}

        {properties.address && (
          <div
            itemProp='address'
            itemScope
            itemType='https://schema.org/PostalAddress'>
            <meta
              itemProp='streetAddress'
              content={`${properties.address.streetNumber || ''} ${
                properties.address.streetName || ''
              }`}
            />
            <meta itemProp='postalCode' content={properties.address.zipcode} />
            <meta
              itemProp='addressCountry'
              content={properties.address.countryCode}
            />
            <meta
              itemProp='addressLocality'
              content={properties.address.city}
            />
            <meta itemProp='addressRegion' content={properties.address.state} />
          </div>
        )}
        {properties.INTPTLAT && properties.INTPTLON && (
          <div
            itemProp='geo'
            itemScope
            itemType='https://schema.org/GeoCoordinates'>
            <meta itemProp='latitude' content={properties.INTPTLAT} />
            <meta itemProp='longitude' content={properties.INTPTLON} />
          </div>
        )}
        <meta
          itemProp='hasMap'
          content={
            document.location.href.substring(
              document.location.href.indexOf('#')
            ) + '#map'
          }
        />
        <link
          rel='stylesheet'
          type='text/css'
          href='https://cdnjs.cloudflare.com/ajax/libs/slick-carousel/1.6.0/slick.min.css'
        />
        <link
          rel='stylesheet'
          type='text/css'
          href='https://cdnjs.cloudflare.com/ajax/libs/slick-carousel/1.6.0/slick-theme.min.css'
        />
        {needShadowCSS && (
          <>
            <style className='liveby-css liveby-shadow-root-css'>
              {styles}
            </style>
            <link
              rel='stylesheet'
              type='text/css'
              href={
                process.env.NODE_ENV === 'dev'
                  ? 'https://dev.profiles.liveby.com/static/css/shadow-dom.css'
                  : 'https://pages.liveby.com/static/css/shadow-dom.css'
              }
            />
            {this.props.template &&
              this.props.template.properties.customStyles && (
                <link
                  rel='stylesheet'
                  type='text/css'
                  href={this.props.template.properties.customStyles}
                />
              )}
          </>
        )}
        {this.components.map(({ Component, properties }, index) => (
          <Component
            {...this.props}
            {...this.state}
            {...properties}
            key={index}
            setProp={this.setProp}
            sendAnalytics={this.sendAnalytics}
          />
        ))}
      </article>
    )
  }
}

function mapDimensionsToProps({ width }) {
  // Master list of breakpoints (keys) and columns (values), BEWARE!
  const MIN_WIDTH_THRESHOLD = 100
  const breakpoints = {
    100: '1',
    200: '1',
    300: '1',
    400: '1',
    500: '2',
    600: '2',
    700: '3',
    800: '3',
    900: '4',
    1000: '4',
    1100: '4',
    1200: '4',
    9999: '4-centered'
  }
  let usableWidth = width
  if (!width || width < MIN_WIDTH_THRESHOLD) {
    const windowWidth = window.innerWidth
    usableWidth = windowWidth
  }
  return {
    queryClassNames: Object.keys(breakpoints).map((key) =>
      usableWidth <= key
        ? `container-query--max-width-${key} container-query--columns-${breakpoints[key]}`
        : false
    )
  }
}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false }
  }

  componentDidCatch(error, info) {
    fetch(
      `https://${process.env.REACT_APP_API_ENDPOINT}/v1/client-error-reporter`,
      {
        method: 'POST',
        body: JSON.stringify({
          error: `${error.stack}\n\nIn Component:\n${info.componentStack}`
        })
      }
    )
    this.setState({ hasError: true })
  }

  render() {
    if (this.state.hasError) {
      // Possibly render a fallback UI
      return null
    }
    return this.props.children
  }
}

export default withLocalize(componentQueries(mapDimensionsToProps)(Profile))
